import { useCallback, useEffect, useRef, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

import type {
  SandboxFleetSettingsResponse,
  SandboxPoolResponse,
  SandboxRegistryResponse,
} from '../lib/api-client';

/**
 * Sandbox Fleet ViewModels. Pools poll (they are live infrastructure — ready
 * counts move on their own); registries and settings load on demand and
 * refresh after writes. Hook discipline per use-sandbox-runtimes: status
 * union, in-flight guard, hidden-tab pause.
 */

type Status = 'idle' | 'loading' | 'success' | 'error';

export function useSandboxPools({ pollInterval = 15_000 }: { pollInterval?: number } = {}) {
  const apiClient = useApiClient();
  const [pools, setPools] = useState<SandboxPoolResponse[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();
  const inFlight = useRef(false);

  const refresh = useCallback(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    setStatus((s) => (s === 'success' ? s : 'loading'));
    apiClient
      .listSandboxPools({ limit: 100 })
      .then((res) => {
        setPools(res.data);
        setStatus('success');
        setError(undefined);
      })
      .catch((err) => {
        setStatus('error');
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [apiClient]);

  useEffect(() => {
    refresh();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, pollInterval);
    return () => clearInterval(id);
  }, [refresh, pollInterval]);

  return { pools, status, error, refresh };
}

export function useSandboxRegistries() {
  const apiClient = useApiClient();
  const [registries, setRegistries] = useState<SandboxRegistryResponse[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();

  const refresh = useCallback(() => {
    setStatus((s) => (s === 'success' ? s : 'loading'));
    apiClient
      .listSandboxRegistries()
      .then((res) => {
        setRegistries(res.data);
        setStatus('success');
        setError(undefined);
      })
      .catch((err) => {
        setStatus('error');
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [apiClient]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { registries, status, error, refresh };
}

export function useSandboxFleetSettings() {
  const apiClient = useApiClient();
  const [settings, setSettings] = useState<SandboxFleetSettingsResponse | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();

  const refresh = useCallback(() => {
    setStatus((s) => (s === 'success' ? s : 'loading'));
    apiClient
      .getSandboxFleetSettings()
      .then((res) => {
        setSettings(res);
        setStatus('success');
        setError(undefined);
      })
      .catch((err) => {
        setStatus('error');
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [apiClient]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { settings, status, error, refresh };
}
